import React, { useState, useEffect, useCallback } from 'react';
import { Key, Save, Trash2, CheckCircle, AlertTriangle, Shield, DollarSign, Scale, ExternalLink, Eye, EyeOff, Loader2, RefreshCw } from 'lucide-react';
import byokService from '../../services/byokService';

const BYOKSettings = () => {
  const [config, setConfig] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  // Form state
  const [byokEnabled, setByokEnabled] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Live model list state
  const [liveModels, setLiveModels] = useState(null); // null = not fetched yet
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelSource, setModelSource] = useState(null); // "live" or "default"

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await byokService.getConfig();
      setConfig(data);
      setProviders(data.providers || []);
      setByokEnabled(data.byok_enabled);
      setSelectedProvider(data.ai_provider || '');
      setSelectedModel(data.ai_model || '');
      setApiKey('');
    } catch (err) {
      console.error('Failed to load BYOK settings:', err);
      setError('Failed to load AI provider settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const getDefaultModelsForProvider = useCallback((providerId) => {
    const provider = providers.find((p) => p.id === providerId);
    return provider ? provider.models : [];
  }, [providers]);

  const fetchModels = useCallback(async (providerId, keyOverride) => {
    if (!providerId) return;

    // Only fetch live if there's a key available (stored or entered)
    const hasKey = keyOverride || (config?.has_api_key && config?.ai_provider === providerId);
    if (!hasKey) {
      setLiveModels(null);
      setModelSource(null);
      return;
    }

    setLoadingModels(true);
    try {
      const result = await byokService.listModels(providerId, keyOverride || undefined);
      setLiveModels(result.models);
      setModelSource(result.source);
    } catch {
      setLiveModels(null);
      setModelSource(null);
    } finally {
      setLoadingModels(false);
    }
  }, [config]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Fetch live models on initial load if provider and key are configured
  useEffect(() => {
    if (config && config.byok_enabled && config.ai_provider && config.has_api_key) {
      fetchModels(config.ai_provider, null);
    }
  }, [config, fetchModels]);

  const availableModelsList = liveModels || getDefaultModelsForProvider(selectedProvider);

  const handleProviderChange = (providerId) => {
    setSelectedProvider(providerId);
    setValidationResult(null);
    setLiveModels(null);
    setModelSource(null);
    setSelectedModel('');
    // Fetch live models if a stored key exists for this provider
    fetchModels(providerId, null);
  };

  const handleValidate = async () => {
    if (!apiKey || !selectedProvider) return;

    setValidating(true);
    setValidationResult(null);
    try {
      const result = await byokService.validateKey(selectedProvider, apiKey);
      setValidationResult(result);
      // If valid, fetch live models using the entered key
      if (result.valid) {
        setLoadingModels(true);
        try {
          const modelResult = await byokService.listModels(selectedProvider, apiKey);
          setLiveModels(modelResult.models);
          setModelSource(modelResult.source);
          // Auto-select first model
          if (modelResult.models.length > 0 && !selectedModel) {
            setSelectedModel(modelResult.models[0].value);
          }
        } catch {
          // ignore — defaults will show
        } finally {
          setLoadingModels(false);
        }
      }
    } catch {
      setValidationResult({ valid: false, error: 'Failed to validate key. Please try again.' });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        byok_enabled: byokEnabled,
        ai_provider: byokEnabled ? selectedProvider : null,
        ai_model: byokEnabled ? selectedModel : null,
      };

      // Only include api_key if the user entered a new one
      if (apiKey) {
        payload.api_key = apiKey;
      }

      const data = await byokService.updateConfig(payload);
      setConfig(data);
      setApiKey('');
      setValidationResult(null);
      setSuccess('AI provider settings saved successfully');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Failed to save BYOK settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveKey = async () => {
    setRemoving(true);
    setError(null);
    try {
      await byokService.removeKey();
      setByokEnabled(false);
      setSelectedProvider('');
      setSelectedModel('');
      setApiKey('');
      setShowRemoveConfirm(false);
      setValidationResult(null);
      await loadConfig();
      setSuccess('API key removed and BYOK disabled');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError('Failed to remove API key');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-3 text-gray-600">Loading AI provider settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Settings Card */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Key className="h-5 w-5 text-gray-500 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">AI Provider Settings</h2>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Configure your own AI API key to use your preferred provider for AI features
          </p>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Use your own API key
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                When disabled, AI features use the JustPractice system key (usage limits may apply)
              </p>
            </div>
            <button
              type="button"
              onClick={() => setByokEnabled(!byokEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${
                byokEnabled ? 'bg-yellow-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  byokEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {byokEnabled && (
            <>
              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AI Provider
                </label>
                <select
                  value={selectedProvider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                >
                  <option value="">Select a provider...</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>

              {/* Model Selection */}
              {selectedProvider && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Model
                    </label>
                    <div className="flex items-center space-x-2">
                      {modelSource && (
                        <span className={`text-xs ${modelSource === 'live' ? 'text-green-600' : 'text-gray-400'}`}>
                          {modelSource === 'live' ? 'Live from API' : 'Default list'}
                        </span>
                      )}
                      {(config?.has_api_key || apiKey) && (
                        <button
                          type="button"
                          onClick={() => fetchModels(selectedProvider, apiKey || undefined)}
                          disabled={loadingModels}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                          title="Refresh model list"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${loadingModels ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                    </div>
                  </div>
                  {loadingModels ? (
                    <div className="flex items-center text-sm text-gray-500 py-2">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading models...
                    </div>
                  ) : availableModelsList.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">
                      Enter and validate your API key to load available models.
                    </p>
                  ) : (
                    <select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                    >
                      {availableModelsList.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* API Key Input */}
              {selectedProvider && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Key
                  </label>
                  {config?.has_api_key && !apiKey && (
                    <div className="mb-2 flex items-center text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>
                        Key configured: <code className="font-mono">{config.api_key_hint}</code>
                        {config.api_key_set_at && (
                          <span className="text-green-600 ml-2">
                            (set {new Date(config.api_key_set_at).toLocaleDateString()})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => {
                        setApiKey(e.target.value);
                        setValidationResult(null);
                      }}
                      placeholder={config?.has_api_key ? 'Enter new key to replace existing...' : 'Enter your API key...'}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm pr-20"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title={showApiKey ? 'Hide key' : 'Show key'}
                      >
                        {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Validate Button */}
                  {apiKey && (
                    <div className="mt-2 flex items-center space-x-3">
                      <button
                        type="button"
                        onClick={handleValidate}
                        disabled={validating}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                      >
                        {validating ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                            Validating...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1.5" />
                            Validate Key
                          </>
                        )}
                      </button>

                      {validationResult && (
                        <span className={`text-sm ${validationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                          {validationResult.valid ? 'Key is valid' : validationResult.error}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Remove Key */}
              {config?.has_api_key && (
                <div className="pt-2 border-t border-gray-100">
                  {showRemoveConfirm ? (
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-600">Remove API key and disable BYOK?</span>
                      <button
                        type="button"
                        onClick={handleRemoveKey}
                        disabled={removing}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      >
                        {removing ? 'Removing...' : 'Yes, remove'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRemoveConfirm(false)}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowRemoveConfirm(true)}
                      className="inline-flex items-center text-sm text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove API key
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Error / Success Messages */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
              {success}
            </div>
          )}

          {/* Save Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving || (byokEnabled && !selectedProvider)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>

      {/* Disclaimers */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Important Notices
          </h3>
        </div>
        <div className="p-6 space-y-5">
          {/* Cost Responsibility */}
          <div className="flex items-start space-x-3">
            <DollarSign className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">Cost Responsibility</h4>
              <p className="text-sm text-gray-600 mt-1">
                Your firm is solely responsible for all API usage costs incurred through your own
                API key. JustPractice has no visibility into your API account and cannot monitor or
                limit spending on your behalf. We recommend setting spending limits directly with
                your provider.
              </p>
            </div>
          </div>

          {/* Data Privacy & Training */}
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">Data Privacy & Model Training</h4>
              <p className="text-sm text-gray-600 mt-1">
                Review and configure your provider's data retention and training policies.
                Client data sent to AI providers may be subject to their data handling terms.
              </p>
              <ul className="mt-2 text-sm text-gray-600 space-y-1">
                <li className="flex items-center">
                  <span className="mr-2">-</span>
                  <span>
                    <strong>Anthropic:</strong> API data is not used for model training by default.{' '}
                    <a href="https://www.anthropic.com/policies/privacy" target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:underline inline-flex items-center">
                      Privacy Policy <ExternalLink className="h-3 w-3 ml-0.5" />
                    </a>
                  </span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2">-</span>
                  <span>
                    <strong>OpenAI:</strong> API data is not used for training by default, but verify
                    your organization settings.{' '}
                    <a href="https://openai.com/enterprise-privacy" target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:underline inline-flex items-center">
                      Enterprise Privacy <ExternalLink className="h-3 w-3 ml-0.5" />
                    </a>
                  </span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2">-</span>
                  <span>
                    <strong>Google:</strong> Review the Gemini API data governance terms for your region.{' '}
                    <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-yellow-700 hover:underline inline-flex items-center">
                      API Terms <ExternalLink className="h-3 w-3 ml-0.5" />
                    </a>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Regulatory Compliance */}
          <div className="flex items-start space-x-3">
            <Scale className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">Regulatory Compliance</h4>
              <p className="text-sm text-gray-600 mt-1">
                Law firms must ensure compliance with applicable bar rules, client confidentiality
                obligations, and data protection regulations (e.g., ABA Model Rules, GDPR, state
                privacy laws) when transmitting client data to third-party AI services. Consider
                consulting your firm's ethics counsel before enabling AI features with client data.
              </p>
            </div>
          </div>

          {/* Security */}
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">Security</h4>
              <p className="text-sm text-gray-600 mt-1">
                API keys are encrypted at rest and are never displayed after being saved. Never share
                your API key with anyone. If you suspect your key has been compromised, revoke it
                immediately in your provider's dashboard and configure a new one here.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BYOKSettings;
